import { useCallback, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../config/queryKeys'
import { getScheduleSettings } from '../services/competition/getScheduleSettings'
import { SportSettings } from '../types'

/**
 * Hook result interface for useScheduleSettings
 */
interface UseScheduleSettingsResult {
  /**
   * The schedule settings data
   */
  scheduleSettings: SportSettings | null
  /**
   * Loading state for the request
   */
  loading: boolean
  /**
   * Error state if request fails
   */
  error: Error | null
  /**
   * Function to manually fetch schedule settings
   */
  fetchScheduleSettings: (scheduleId: string | number) => Promise<void>
  /**
   * Function to clear schedule settings (resets to null)
   */
  clearScheduleSettings: () => void
}

/**
 * Hook for fetching and managing schedule settings.
 *
 * Backed by React Query under `scheduleSettings.bySchedule`, so every reader
 * of one competition's settings — the Schedule page, its Standings and
 * Statistics tabs, the game form — shares one request and one cache entry
 * instead of each fetching its own copy.
 *
 * - `fetchScheduleSettings(id)` for a NEW id reads the cache first.
 * - Calling it again for the id already shown is a refresh (the Schedule page
 *   does this after a settings save), so that call always hits the server.
 * - It never throws; a failure lands in `error`, as before.
 */
export const useScheduleSettings = (): UseScheduleSettingsResult => {
  const queryClient = useQueryClient()
  const [scheduleId, setScheduleId] = useState<string | number | null>(null)
  const scheduleIdRef = useRef<string | number | null>(null)

  const query = useQuery({
    queryKey: queryKeys.scheduleSettings.bySchedule(scheduleId),
    queryFn: () => getScheduleSettings(scheduleId as string | number),
    enabled: Boolean(scheduleId),
  })

  /**
   * Fetches schedule settings for a given schedule ID
   * @param nextScheduleId - The ID of the schedule to fetch settings for
   */
  const fetchScheduleSettings = useCallback(
    async (nextScheduleId: string | number) => {
      if (!nextScheduleId) {
        return
      }
      const queryKey = queryKeys.scheduleSettings.bySchedule(nextScheduleId)
      const isRefresh = scheduleIdRef.current === nextScheduleId
      scheduleIdRef.current = nextScheduleId
      setScheduleId(nextScheduleId)
      try {
        if (isRefresh) {
          // Joins a request already in flight rather than firing a second one.
          await queryClient.refetchQueries(
            { queryKey, exact: true },
            { cancelRefetch: false }
          )
        } else {
          await queryClient.fetchQuery({
            queryKey,
            queryFn: () => getScheduleSettings(nextScheduleId),
          })
        }
      } catch {
        // Surfaced through `error` by the query itself.
      }
    },
    [queryClient]
  )

  /**
   * Clears schedule settings (resets to null)
   */
  const clearScheduleSettings = useCallback(() => {
    scheduleIdRef.current = null
    setScheduleId(null)
  }, [])

  return {
    scheduleSettings: scheduleId ? query.data ?? null : null,
    loading: query.isFetching,
    error: scheduleId ? query.error : null,
    fetchScheduleSettings,
    clearScheduleSettings,
  }
}
